import { Coordinates } from '../../modules/stores/types';

const SUCCESS_GEOCODING_STATUSES = ['OK'];

export type GeoRegion = {
  state?: string;
  zipCode?: string;
};

type AddressComponent = {
  types: string[];
  short_name: string;
};

type GeocodeResult = {
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
  address_components?: AddressComponent[];
};

type GeocodeRequest = {
  address?: string;
  location?: Coordinates;
};

const getAddressComponent = (components: AddressComponent[] | undefined, type: string): string | undefined =>
  components?.find((component) => component.types.includes(type))?.short_name;

export class Geocoder {
  static isGeocoderAvailable() {
    if (typeof google === 'undefined') {
      return false;
    }

    return Boolean(google?.maps?.Geocoder);
  }

  static getCoordinates(search: string): Promise<Coordinates | null> {
    return Geocoder.geocode({ address: search }, (result) => ({
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    }));
  }

  static getGeoRegion(coordinates: Coordinates): Promise<GeoRegion | null> {
    return Geocoder.geocode({ location: coordinates }, (result) => {
      const state = getAddressComponent(result.address_components, 'administrative_area_level_1');
      const zipCode = getAddressComponent(result.address_components, 'postal_code');

      if (!state && !zipCode) {
        return null;
      }

      return { state, zipCode };
    });
  }

  private static geocode<T>(
    request: GeocodeRequest,
    mapResult: (result: GeocodeResult) => T | null,
  ): Promise<T | null> {
    return new Promise<T | null>((resolve, reject) => {
      try {
        const gg = new google.maps.Geocoder();
        gg.geocode(request, (res: GeocodeResult[] | null, status: string) => {
          if (!SUCCESS_GEOCODING_STATUSES.includes(status) || !res?.[0]) {
            resolve(null);
            return;
          }

          resolve(mapResult(res[0]));
        });
      } catch (e) {
        reject(e);
      }
    });
  }
}
